import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  Chip,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Refresh,
} from '@mui/icons-material';
import { financeService } from '../../services/financeService';
import { AgeingReport } from '../../types/finance';
import {
  ReportHeader,
  ExportToolbar,
  IndianAmountDisplay,
} from '../../components/finance';

const AgeingReportsPage: React.FC = () => {
  const [reportType, setReportType] = useState<'RECEIVABLES' | 'PAYABLES'>('RECEIVABLES');
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ageingReport, setAgeingReport] = useState<AgeingReport | null>(null);

  const handleReportTypeChange = (
    _: React.MouseEvent<HTMLElement>,
    newType: 'RECEIVABLES' | 'PAYABLES' | null
  ) => {
    if (newType !== null) {
      setReportType(newType);
      setAgeingReport(null);
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = reportType === 'RECEIVABLES'
        ? await financeService.getReceivablesAgeing(asOnDate)
        : await financeService.getPayablesAgeing(asOnDate);
      setAgeingReport(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load ageing report');
    } finally {
      setLoading(false);
    }
  };

  const getBucketColor = (index: number, total: number) => {
    const colors = ['success', 'info', 'warning', 'error', 'error'];
    return colors[Math.min(index, colors.length - 1)] as 'success' | 'info' | 'warning' | 'error';
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" color="primary" sx={{ fontWeight: 'bold' }}>
            Ageing Reports
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Receivables and Payables ageing analysis by due date buckets
          </Typography>
        </Box>
        {ageingReport && (
          <ExportToolbar
            onExportPDF={() => {}}
            onExportExcel={() => {}}
            disabled={loading}
          />
        )}
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="flex-end">
          <Grid size={{ xs: 12, sm: 4 }}>
            <Typography variant="subtitle2" gutterBottom>Report Type</Typography>
            <ToggleButtonGroup
              value={reportType}
              exclusive
              onChange={handleReportTypeChange}
              fullWidth
              size="small"
            >
              <ToggleButton value="RECEIVABLES" color="success">
                <TrendingUp sx={{ mr: 1 }} />
                Receivables
              </ToggleButton>
              <ToggleButton value="PAYABLES" color="error">
                <TrendingDown sx={{ mr: 1 }} />
                Payables
              </ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              label="As On Date"
              type="date"
              size="small"
              fullWidth
              value={asOnDate}
              onChange={(e) => setAsOnDate(e.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <Button
              variant="contained"
              fullWidth
              onClick={fetchReport}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={16} /> : <Refresh />}
              color={reportType === 'RECEIVABLES' ? 'success' : 'error'}
            >
              Generate Report
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Report Content */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : ageingReport ? (
        <>
          <ReportHeader
            companyName="Your Company Name"
            reportTitle={`${reportType === 'RECEIVABLES' ? 'Receivables' : 'Payables'} Ageing Report`}
            asOnDate={ageingReport.asOnDate}
          />

          {/* Summary Cards */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card sx={{ bgcolor: reportType === 'RECEIVABLES' ? 'success.50' : 'error.50' }}>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Outstanding
                  </Typography>
                  <IndianAmountDisplay
                    amount={ageingReport.summary.totalOutstanding}
                    bold
                    size="large"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Total Items
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {ageingReport.summary.totalItems}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <Card>
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary">
                    Avg Days Outstanding
                  </Typography>
                  <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                    {ageingReport.summary.averageDaysOutstanding.toFixed(0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Bucket Summary */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
              Ageing Buckets
            </Typography>
            <Grid container spacing={2}>
              {ageingReport.buckets.map((bucket, index) => (
                <Grid key={index} size={{ xs: 12, sm: 12 / ageingReport.buckets.length }}>
                  <Card
                    sx={{
                      textAlign: 'center',
                      border: '2px solid',
                      borderColor: `${getBucketColor(index, ageingReport.buckets.length)}.main`,
                    }}
                  >
                    <CardContent>
                      <Chip
                        label={bucket.label}
                        size="small"
                        color={getBucketColor(index, ageingReport.buckets.length)}
                        sx={{ mb: 1 }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                        {bucket.count}
                      </Typography>
                      <IndianAmountDisplay amount={bucket.amount} bold />
                      <Box sx={{ mt: 1 }}>
                        <LinearProgress
                          variant="determinate"
                          value={bucket.percentage}
                          color={getBucketColor(index, ageingReport.buckets.length)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="caption" color="text.secondary">
                          {bucket.percentage.toFixed(1)}%
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* Detail Table */}
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: reportType === 'RECEIVABLES' ? 'success.main' : 'error.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    {reportType === 'RECEIVABLES' ? 'Customer' : 'Vendor'}
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Invoice No.</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Invoice Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Due Date</TableCell>
                  <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>Days</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Bucket</TableCell>
                  <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold' }}>Amount (₹)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ageingReport.items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">No outstanding items found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  ageingReport.items.map((item, index) => (
                    <TableRow key={index} sx={{ '&:hover': { bgcolor: 'grey.50' } }}>
                      <TableCell sx={{ fontWeight: 500 }}>{item.partyName}</TableCell>
                      <TableCell>{item.invoiceNumber || '-'}</TableCell>
                      <TableCell>
                        {new Date(item.invoiceDate).toLocaleDateString('en-IN')}
                      </TableCell>
                      <TableCell>
                        {new Date(item.dueDate).toLocaleDateString('en-IN')}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={item.daysOutstanding}
                          size="small"
                          color={item.daysOutstanding > 90 ? 'error' : item.daysOutstanding > 30 ? 'warning' : 'success'}
                        />
                      </TableCell>
                      <TableCell>{item.bucket}</TableCell>
                      <TableCell align="right">
                        <IndianAmountDisplay amount={item.amount} bold />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          {reportType === 'RECEIVABLES' ? (
            <TrendingUp sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
          ) : (
            <TrendingDown sx={{ fontSize: 60, color: 'grey.300', mb: 2 }} />
          )}
          <Typography variant="h6" color="text.secondary">
            Select report type and date, then click Generate
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default AgeingReportsPage;
